// src/components/chat/ExcelCsvGuidelinesModal.tsx
import { useContext, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import useElysiaStore from '@/app/elysia-store';
import { AuthContext } from '@/context/AuthContext';
import { SettingContext } from '@/context/SettingContext';
import { updateDefaultSettings } from '@/services/content';
import { encodeBase64 } from '@/utils/chat';
import notify from '@/services/notify';

const GUIDELINES = [
  'Ensure your data is in a tabular format, with rows representing records and columns representing fields.',
  'If a sheet contains multiple tables, separate each table with a blank row.',
  'Use clear and meaningful names for both columns and tables.',
  'Only structured, column-aligned tables will be processed.',
  'Sheets containing pivot tables or charts will be automatically skipped.',
  'Applied filters in Excel will not impact the data being read.',
];

const ExcelCsvGuidelinesModal = () => {
  const show = useElysiaStore((state) => state.showGuidelinesModal);
  const acknowledged = useElysiaStore((state) => state.guidelinesAcknowledged);
  const setShow = useElysiaStore.getState().setShowGuidelinesModal;
  const setAcknowledged = useElysiaStore.getState().setGuidelinesAcknowledged;

  const preferredTone = useElysiaStore((state) => state.preferredTone);
  const customInstructions = useElysiaStore((state) => state.customInstructions);
  const memory = useElysiaStore((state) => state.memory);
  const { defaultModel, defaultLanguage, defaultTheme } = useContext(SettingContext);

  const { profileInfo } = useContext(AuthContext);
  const userId = profileInfo?.userId;

  const [checked, setChecked] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // the checkbox only shows while not acknowledged, so it always starts unticked
  useEffect(() => {
    if (show) setChecked(false);
  }, [show]);

  const handleSave = async () => {
    if (!userId) return;
    setIsSaving(true);
    try {
      const settingData = {
        name_of_model: defaultModel.name_of_model,
        response_language: defaultLanguage,
        preferred_theme: defaultTheme,
        preferred_tone: preferredTone,
        custom_instructions: encodeBase64(customInstructions),
        memory: memory?.enabled,
        csv_excel_criteria_model_acknowledged: true,
      };
      const response = await updateDefaultSettings(userId, settingData);
      notify({
        message: response?.data?.message,
        type: 'success',
      });
      setAcknowledged(true);
      setShow(false);
    } catch (e) {
      console.error(e);
      notify({ message: 'Could not save your preference. Please try again.', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      centered
      dialogClassName="elysia-modal excel-guidelines-modal"
      data-testid="excel-guidelines-modal"
      backdropClassName="custom-backdrop"
      backdrop={true}
    >
      <Modal.Header closeButton>
        <Modal.Title as="h5">Excel &amp; CSV - Upload Guidelines</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <ul className={acknowledged ? 'mb-0' : 'mb-4'}>
          {GUIDELINES.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
        {!acknowledged && (
          <Form.Check
            type="checkbox"
            label="I acknowledge, don't show this message again"
            className="elysia-checkbox"
            checked={checked}
            disabled={isSaving}
            onChange={(e) => setChecked(e.target.checked)}
          />
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <Button variant="secondary" onClick={() => setShow(false)} disabled={isSaving}>
          Back
        </Button>
        {!acknowledged && (
          <Button
            onClick={handleSave}
            disabled={!checked || isSaving}
            data-testid="excel-guidelines-save"
            variant="primary"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ExcelCsvGuidelinesModal;
